import React from 'react';
import { Alert, Button, Popover, Space, Typography } from 'antd';
import { SettingOutlined } from '@ant-design/icons';
import ChatComposer from '../../ChatComposer.jsx';
import preserveInputFocus from '../../composer/preserveInputFocus.js';
import { ChatWorkspaceSectionShell } from '../layout/index.js';
import ChatWorkspaceCanonicalActions from './ChatWorkspaceCanonicalActions.jsx';
import {
  ChatWorkspaceAuthoritativePendingTip,
  ChatWorkspaceIntakeRunningTip,
  ChatWorkspaceModeSuggestionTip,
} from './ChatWorkspaceModeTips.jsx';
import LatestMessageButton from './LatestMessageButton.jsx';

const { Text } = Typography;
import { conditionalValue, firstTruthy, normalizeComposerProps, resolveSendOutcomeFeedback, resolveComposerSectionState } from "./composerSectionPresentation.js";
export { resolveSendOutcomeFeedback } from "./composerSectionPresentation.js";
export default function ChatWorkspaceComposerSection(props) {
  const {
    canvasWorkspaceFullscreenMode,
    themeTokens,
    isCompactLayout,
    contentMaxWidth,
    recommendationPanelState,
    effectiveComposerChooser,
    streamLoading,
    sendSubmitting,
    shouldRenderModeSuggestion,
    modeSuggestion,
    modeSuggestionAction,
    modeSuggestionActions,
    canonicalActionEntries,
    onCanonicalActionSelect,
    modeSuggestionFromFallback,
    onModeSuggestionAction,
    handleComposerActionSelect,
    onComposerChooserSubmit,
    isStatusOnlyModeSuggestion,
    statusOnlyTipHidden,
    setStatusOnlyTipHidden,
    applyBehaviorEvent,
    isRequirementIntakeMode,
    chooserAwaitingAuthoritative,
    canonicalAwaitingReason,
    patchEventHealth,
    showCollectingAnalysisCta,
    collectingAnalysisAction,
    onCollectingAnalysisAction,
    attachmentActionError,
    sendOutcome,
    resolvedUILabels,
    attachmentConstraintsHint,
    composerInputRef,
    sendDisabled,
    modeSwitchOptions,
    resolvedActiveModeKey,
    modeStatusByKey,
    handleAttachFiles,
    inputState,
    handleModeSelect,
    handleSend,
    handleAbort,
    resolvedComposerPlaceholder,
    runtimeComposerActions,
    onComposerChooserDismiss,
    sendButtonLabel,
    resolvedUserDisplayName, displayPolicy,
    intentEscalationPolicyPopoverContent,
    intentEscalationPolicyPopoverOpen,
    handleIntentEscalationPolicyPopoverOpenChange,
    intentEscalationConfigEntryLabel,
    contextGaugePopoverContent,
    contextGaugeHeadline,
    contextWindowPercent,
    contextActivityState,
    contextActivityStatus,
    latestUnreadCue,
    activeTargetChip,
    onActiveTargetDismiss, onOpenSkillManagement,
  } = {
    sendSubmitting: false, modeSuggestionActions: [], canonicalActionEntries: [], modeSuggestionFromFallback: false,
    sendDisabled: false, displayPolicy: {}, contextGaugeHeadline: '', contextWindowPercent: 0,
    contextActivityState: '', contextActivityStatus: '', latestUnreadCue: null, activeTargetChip: null,
    onActiveTargetDismiss: null, ...normalizeComposerProps(props),
  };
  const labels = Object.assign({}, resolvedUILabels);
  const input = Object.assign({}, inputState);
  const chooser = Object.assign({}, effectiveComposerChooser);
  const patchHealth = Object.assign({}, patchEventHealth);
  const attachmentError = Object.assign({}, attachmentActionError);
  const sendOutcomeFeedback = resolveSendOutcomeFeedback(sendOutcome);
  const sectionState = resolveComposerSectionState({
    modeSuggestion,
    modeSuggestionAction,
    effectiveComposerChooser,
    statusOnlyTipHidden,
    shouldRenderModeSuggestion,
    modeSuggestionFromFallback,
    streamLoading,
    sendSubmitting,
    recommendationPanelState,
    canonicalActionEntries,
    latestUnreadCue,
    intentEscalationPolicyPopoverContent,
    intentEscalationConfigEntryLabel,
    contextActivityState,
    contextActivityStatus,
    contextGaugeHeadline,
  });
  const {
    composerBusy,
    contextGaugeShortLabel,
    contextGaugeState,
    contextGaugeStatus,
    latestCue,
    resolvedModeSuggestion,
    resolvedModeSuggestionAction,
    shouldShowCanonicalActions,
    shouldShowIntentEscalationConfigEntry,
    shouldShowLatestUnreadCue,
    shouldShowModeSuggestion,
    suggestionAllowedByPanelState,
  } = sectionState;
  const contextGaugeClassName = [
    'flare-workspace-context-gauge',
    conditionalValue(Boolean(contextGaugeState), `is-${contextGaugeState}`, ''),
    conditionalValue(Boolean(contextGaugeStatus), `is-${contextGaugeStatus}`, ''),
  ].filter(Boolean).join(' ');
  return (
    <ChatWorkspaceSectionShell
      background={conditionalValue(canvasWorkspaceFullscreenMode, 'transparent', themeTokens.appBg)}
      className="flare-workspace-composer-section"
      contentMaxWidth={contentMaxWidth}
      padding={conditionalValue(isCompactLayout, '0 12px 12px', '12px 24px 14px')}
    >
      {conditionalValue([!composerBusy, shouldShowModeSuggestion, suggestionAllowedByPanelState].every(Boolean), (
        <ChatWorkspaceModeSuggestionTip
          modeSuggestion={resolvedModeSuggestion}
          modeSuggestionAction={resolvedModeSuggestionAction}
          modeSuggestionActions={modeSuggestionActions}
          resolvedActiveModeKey={resolvedActiveModeKey}
          modeStatusByKey={modeStatusByKey}
          handleComposerActionSelect={(actionItem) => {
            if (typeof onModeSuggestionAction === 'function') {
              onModeSuggestionAction(actionItem);
              return;
            }
            handleComposerActionSelect?.(actionItem);
          }}
          isStatusOnlyModeSuggestion={isStatusOnlyModeSuggestion}
          setStatusOnlyTipHidden={setStatusOnlyTipHidden}
          applyBehaviorEvent={applyBehaviorEvent}
        />
      ))}
      {conditionalValue(shouldShowCanonicalActions, (
        <ChatWorkspaceCanonicalActions
          actionEntries={canonicalActionEntries}
          handleCanonicalActionSelect={(actionItem) => {
            if (typeof onCanonicalActionSelect === 'function') {
              onCanonicalActionSelect(actionItem);
              return;
            }
            handleComposerActionSelect?.(actionItem);
          }}
        />
      ))}
      {conditionalValue([
        !effectiveComposerChooser,
        isRequirementIntakeMode,
        chooserAwaitingAuthoritative,
        !composerBusy,
        !shouldShowModeSuggestion,
      ].every(Boolean), (
        <ChatWorkspaceAuthoritativePendingTip
          canonicalAwaitingReason={canonicalAwaitingReason}
        />
      ))}
      {conditionalValue([
        effectiveComposerChooser,
        isRequirementIntakeMode,
        !composerBusy,
        !String(chooser.key ?? '').startsWith('decision-point:'),
      ].every(Boolean), (
        <ChatWorkspaceIntakeRunningTip />
      ))}
      {conditionalValue(showCollectingAnalysisCta, (
        <div className="flare-workspace-analysis-cta" style={{ marginBottom: 8 }}>
          <button
            className="flare-workspace-analysis-cta-button is-pulsing"
            type="button"
            onClick={() => {
              if (!collectingAnalysisAction) {
                return;
              }
              if (typeof onCollectingAnalysisAction === 'function') {
                onCollectingAnalysisAction(collectingAnalysisAction);
                return;
              }
              handleComposerActionSelect?.({
                source: 'runtime_action',
                text: '开始需求分析',
                action: collectingAnalysisAction,
              });
            }}
          >
            开始需求分析（已达 80% 阈值）
          </button>
        </div>
      ))}
      {conditionalValue(Boolean(attachmentActionError), (
        <div style={{ marginBottom: 8 }}>
          <Alert
            message={firstTruthy(attachmentError.message, String(attachmentActionError))}
            showIcon
            type="error"
          />
        </div>
      ))}
      {conditionalValue(Boolean(sendOutcomeFeedback), (
        <div style={{ marginBottom: 8 }}>
          <Alert
            message={sendOutcomeFeedback?.message}
            showIcon
            type={sendOutcomeFeedback?.type}
          />
        </div>
      ))}
      <div style={{ position: 'relative' }}>
        {conditionalValue(shouldShowLatestUnreadCue, (
          <div
            style={{
              left: '50%',
              position: 'absolute',
              top: -20,
              transform: 'translateX(-50%)',
              zIndex: 3,
            }}
          >
            <LatestMessageButton
              label={firstTruthy(latestCue.label, '查看最新')}
              onReadLatest={latestCue.onReadLatest}
              themeTokens={themeTokens}
            />
          </div>
        ))}
        <ChatComposer
          attachLabel={firstTruthy(labels.composer_attach_label, '上传文件')}
          attachmentConstraintsHint={attachmentConstraintsHint}
          disabled={streamLoading}
          embedded
          fileList={input.fileList}
          hintText={firstTruthy(labels.composer_hint, '按回车发送，按 Shift + 回车换行')}
          inputRef={composerInputRef}
          loading={streamLoading}
          submitting={sendSubmitting}
          modeOptions={modeSwitchOptions}
          modeStatusByKey={modeStatusByKey}
          modeValue={resolvedActiveModeKey}
          onAttachFiles={handleAttachFiles}
          onChange={input.handleInputChange}
          onModeChange={handleModeSelect}
          onAbort={handleAbort} onOpenSkillManagement={onOpenSkillManagement}
          onComposerActionSelect={handleComposerActionSelect}
          onComposerChooserSubmit={onComposerChooserSubmit}
          onComposerChooserDismiss={onComposerChooserDismiss}
          onRemoveFile={input.handleRemoveFile}
          onSend={handleSend}
          placeholder={resolvedComposerPlaceholder}
          composerActions={runtimeComposerActions}
          composerChooser={effectiveComposerChooser}
          shortcutAction={modeSuggestionAction}
          shortcutActions={modeSuggestionActions}
          sendLabel={sendButtonLabel}
          sendDisabled={sendDisabled}
          themeTokens={themeTokens}
          value={input.inputValue}
          activeTargetChip={activeTargetChip}
          onActiveTargetDismiss={onActiveTargetDismiss}
        />
      </div>
      <div className="flare-workspace-footer-status">
        <div className="flare-workspace-footer-left">
          {conditionalValue(displayPolicy.showUserFooter === false, null, <Text style={{ color: themeTokens.textSecondary, fontSize: conditionalValue(isCompactLayout, 12, 11) }} type="secondary">{`${firstTruthy(labels.user_footer_label, '用户')}：${resolvedUserDisplayName}`}</Text>)}
          {conditionalValue([patchHealth.ok === false, !effectiveComposerChooser].every(Boolean), (
            <Text style={{ color: themeTokens.error, fontSize: 12 }}>
              {`主流程状态未就绪（${firstTruthy(patchHealth.invalidReason, 'patch_event_rejected')}）`}
            </Text>
          ))}
          {shouldShowIntentEscalationConfigEntry ? (
            <Popover
              content={intentEscalationPolicyPopoverContent}
              open={intentEscalationPolicyPopoverOpen}
              classNames={{ root: 'flare-workspace-hub-popover' }}
              placement="topLeft"
              trigger="click"
              onOpenChange={handleIntentEscalationPolicyPopoverOpenChange}
            >
              <Button
                aria-label={intentEscalationConfigEntryLabel}
                className="flare-workspace-footer-config-entry"
                onMouseDown={preserveInputFocus}
                size="small"
                type="text"
              >
                <Space align="center" size={4}>
                  <SettingOutlined />
                  <span>{intentEscalationConfigEntryLabel}</span>
                </Space>
              </Button>
            </Popover>
          ) : null}
        </div>
        <div className="flare-workspace-context-status">
          {conditionalValue(Boolean(contextGaugeShortLabel), (
            <Text className="flare-workspace-context-headline" type="secondary">
              {contextGaugeShortLabel}
            </Text>
          ))}
          <Popover
            color="#f8fbff"
            content={contextGaugePopoverContent}
            classNames={{ root: 'flare-workspace-context-popover-overlay' }}
            placement="topRight"
            trigger="click"
          >
            <button
              aria-label={contextWindowPercent == null ? '会话上下文占用不可用' : `会话上下文占用 ${contextWindowPercent}%`}
              className="flare-workspace-context-gauge-button"
              onMouseDown={preserveInputFocus}
              type="button"
            >
              <span
                className={contextGaugeClassName}
                role="img"
                style={{ '--flare-context-ratio': contextWindowPercent == null ? undefined : `${contextWindowPercent}%` }}
              >
                <span className="flare-workspace-context-gauge-core" />
              </span>
            </button>
          </Popover>
        </div>
      </div>
    </ChatWorkspaceSectionShell>
  );
}
